/**
 * LoadingScreen - Full-screen loading overlay with spinner animation.
 * Displays during world initialization and fades out when complete.
 */

import type { World } from '../game/World.js';

export interface ILoadingScreen {
    start(world: World): void;
    stop(): void;
}

/**
 * Default loading screen implementation with simple HTML/CSS
 */
export class DefaultLoadingScreen implements ILoadingScreen {
  private container: HTMLElement | null = null;
  private isActive = false;

  public start(world: World): void {
    if (this.isActive) {
      return;
    }

    this.createLoadingScreen(world);
    this.isActive = true;
  }

  public stop(): void {
    if (!this.isActive || !this.container) {
      return;
    }

    // Fade out animation
    this.container.style.opacity = '0';

    setTimeout(() => {
      if (this.container && this.container.parentNode) {
        this.container.parentNode.removeChild(this.container);
      }
      this.container = null;
      this.isActive = false;
    }, 300);
  }

  private createLoadingScreen(world: World): void {
    // Create main container
    this.container = document.createElement('div');
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: black;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: white;
      opacity: 1;
      transition: opacity 0.3s ease-in-out;
    `;

    // Create loading content
    const loadingContent = document.createElement('div');
    loadingContent.style.cssText = `
      text-align: center;
      max-width: 400px;
      padding: 2rem;
    `;

    // Create spinner
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 50px;
      height: 50px;
      border: 3px solid rgba(255, 255, 255, 0.3);
      border-top: 3px solid white;
      border-radius: 50%;
      margin: 2rem auto;
      animation: spin 1s linear infinite;
    `;

    // Create loading text
    const loadingText = document.createElement('p');
    loadingText.textContent = 'Loading...';
    loadingText.style.cssText = `
      font-size: 1.1rem;
      margin: 1rem 0 0 0;
      opacity: 0.9;
    `;

    // Add CSS animation for spinner
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    // Assemble the loading screen
    loadingContent.appendChild(spinner);
    loadingContent.appendChild(loadingText);
    this.container.appendChild(loadingContent);

    // Add to game container
    if (world.gameContainer) {
      world.gameContainer.appendChild(this.container);
    }
  }
}

